// src/pages/admin/AdminChips.js — Gerenciamento de Chips
import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, List, ListItemButton,
  ListItemText, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Divider, Chip, Stack, CircularProgress, LinearProgress, Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  FiCpu, FiWifi, FiWifiOff, FiAlertCircle, FiArrowLeft,
  FiCheckCircle, FiClock, FiPhone,
  FiRefreshCw, FiXCircle, FiHelpCircle, FiShield,
} from 'react-icons/fi';
import AppSidebar from '../../components/AppSidebar';
import api from '../../services/Api';

// Mapeamento de status do chip para exibição
const STATUS_CONFIG = {
  ONLINE:  { label: 'Online',      color: '#16a34a', bg: '#f0fdf4',  icon: FiWifi },
  OFFLINE: { label: 'Offline',     color: '#64748b', bg: '#f8fafc',  icon: FiWifiOff },
  ERROR:   { label: 'Com Erro',    color: '#dc2626', bg: '#fef2f2',  icon: FiAlertCircle },
  PENDING: { label: 'Aguardando',  color: '#d97706', bg: '#fffbeb',  icon: FiClock },
};

// Mapeamento do meta_status.overall (derivado no backend) pra visual.
// Fonte única — tanto o badge quanto o modal usam. Se aparecer valor novo,
// adicionar aqui.
const META_STATUS_CONFIG = {
  APPROVED:          { label: 'Aprovado',           color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: FiCheckCircle,
    desc: 'Chip aprovado pela Meta. Business Verification, WABA e código do número validados.' },
  PENDING:           { label: 'Pendente',           color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: FiClock,
    desc: 'Aguardando análise da Meta. O status atualiza sozinho quando eles concluírem.' },
  REJECTED:          { label: 'Rejeitado',          color: '#dc2626', bg: '#fef2f2', border: '#fecaca', icon: FiXCircle,
    desc: 'A Meta rejeitou este chip. Verifique o Business Manager ou entre em contato com o suporte.' },
  CODE_NOT_VERIFIED: { label: 'Verifique o código', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', icon: FiAlertCircle,
    desc: 'O código de verificação do número ainda não foi confirmado (ou expirou). Reenvie o SMS pra reativar o envio.' },
  UNKNOWN:           { label: 'Não sincronizado',   color: '#64748b', bg: '#f8fafc', border: '#e2e8f0', icon: FiHelpCircle,
    desc: 'Ainda não sincronizamos o status deste chip com a Meta. Clique em "Sincronizar" pra buscar agora.' },
};

// Detalhes crus — labels curtas pro modal.
//
// `phone_status` entrou out/26: é o estado FUNCIONAL do número na Meta
// (CONNECTED / DISCONNECTED / ...) e é o campo que explica um chip aparecendo
// offline. Os outros três respondem "a papelada está em ordem?"; só este
// responde "o número está no ar?".
const META_DETAIL_LABEL = {
  phone_status:            'Número no ar (Meta)',
  bv_status:               'Business Verification (BM)',
  waba_review_status:      'Análise da WhatsApp Business',
  code_verification_status: 'Verificação do código do número',
};

// Como o chip foi conectado (Device.origin). Só COEXISTENCE ganha badge: é o
// único que traz conversa do celular, então é o único em que a origem muda o
// que o usuário deve esperar da tela. MANUAL e EMBEDDED_SIGNUP se comportam
// igual, e um selo em todo card viraria ruído.
const ORIGIN_BADGE = {
  COEXISTENCE: {
    label: 'Coexistência',
    color: '#0e7490', bg: '#ecfeff', border: '#a5f3fc',
    title: 'Número que continua funcionando no app do celular. Mensagens '
         + 'enviadas por lá aparecem aqui, e o histórico aprovado na conexão '
         + 'foi importado.',
  },
};

export default function AdminChips() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  // Continua necessário: o AppSidebar recebe `user` pra montar o menu.
  const user  = JSON.parse(localStorage.getItem('user') || '{}');

  const [devices, setDevices]         = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });
  // Modal detalhado de Meta status por chip. null = fechado.
  const [metaModalDevice, setMetaModalDevice] = useState(null);
  const [metaSyncing, setMetaSyncing] = useState(false);

  // ── Carrega chips da empresa ──────────────────────────────────────────────
  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    const fetchDevices = async () => {
      try {
        const res = await api.get('/devices/', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDevices(res.data);
      } catch (err) {
        setMsg({ type: 'error', text: 'Erro ao carregar chips.' });
      } finally {
        setLoadingDevices(false);
      }
    };
    fetchDevices();
  }, [token, navigate]);

  // Set/26: `handleContratarChip` (checkout Stripe do Chip Gerenciado,
  // R$ 49/mês) removido junto com o card e o modal de contratação — o
  // produto não será comercializado. Ver comentário no topo do arquivo.

  const activeChips  = devices.filter(d => d.status === 'ONLINE');
  const offlineChips = devices.filter(d => d.status !== 'ONLINE');

  // Força re-fetch do status Meta pelo backend (chama endpoint /devices/{id}/sync-meta).
  const handleSyncMeta = async (deviceId) => {
    setMetaSyncing(true);
    try {
      const res = await api.post(`/devices/${deviceId}/sync-meta`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Atualiza a lista com o novo meta_status. `device_status` vem junto
      // porque o sync pode mudar o estado do número na Meta, e é dele que o
      // backend deriva o status do chip — sem aplicar aqui, o card seguiria
      // escrito "Offline" logo depois de uma sincronização bem-sucedida.
      // Nunca recalculamos no frontend: só copiamos o que o backend mandou.
      const { device_status: derivedStatus, ...metaStatus } = res.data || {};
      const patch = derivedStatus
        ? { meta_status: metaStatus, status: derivedStatus }
        : { meta_status: metaStatus };
      setDevices(list => list.map(d => d.id === deviceId ? { ...d, ...patch } : d));
      if (metaModalDevice?.id === deviceId) {
        setMetaModalDevice(prev => ({ ...prev, ...patch }));
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Erro ao sincronizar com a Meta.' });
    } finally {
      setMetaSyncing(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <AppSidebar user={user} />

      <Box sx={{ flexGrow: 1, ml: { xs: 0, md: 'var(--sidebar-width, 260px)' }, transition: 'margin-left 0.25s ease', p: { xs: 2, md: 4 }, maxWidth: 900 }}>

        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} mb={4}>
          <Button
            startIcon={<FiArrowLeft />}
            onClick={() => navigate('/dashboard')}
            sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
          >
            Voltar
          </Button>
          <Divider orientation="vertical" flexItem />
          <FiCpu size={22} color="#25d366" />
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0f172a">
              Gerenciamento de Chips
            </Typography>
            <Typography variant="body2" color="#64748b">
              Visualize e gerencie os chips WhatsApp da sua empresa
            </Typography>
          </Box>
        </Stack>

        {msg.text && (
          <Alert severity={msg.type || 'info'} sx={{ mb: 3, borderRadius: 2 }} onClose={() => setMsg({ type: '', text: '' })}>
            {msg.text}
          </Alert>
        )}

        {/* Set/26: aqui ficava o card "Chip Gerenciado pela Cetto" (R$ 49/mês,
            chip Claro provisionado pela equipe) com o botão que abria o modal
            de contratação. Produto descontinuado — não reintroduzir sem a
            operação existir. */}

        {/* Lista de Chips */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700} color="#0f172a">
                Seus Chips
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={`${activeChips.length} online`}
                  size="small"
                  sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 700, fontSize: '0.72rem' }}
                />
                {offlineChips.length > 0 && (
                  <Chip
                    label={`${offlineChips.length} offline`}
                    size="small"
                    sx={{ bgcolor: '#f8fafc', color: '#64748b', fontWeight: 700, fontSize: '0.72rem' }}
                  />
                )}
              </Stack>
            </Stack>
          </Box>

          {loadingDevices ? (
            <Box sx={{ p: 4 }}>
              <LinearProgress sx={{ borderRadius: 2, '& .MuiLinearProgress-bar': { bgcolor: '#25d366' } }} />
              <Typography variant="body2" color="#94a3b8" textAlign="center" mt={2}>
                Carregando chips...
              </Typography>
            </Box>
          ) : devices.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <FiCpu size={40} color="#cbd5e1" />
              <Typography variant="h6" color="#94a3b8" mt={2} fontWeight={600}>
                Nenhum chip cadastrado
              </Typography>
              <Typography variant="body2" color="#cbd5e1" mt={0.5}>
                Conecte um número pela Meta no painel ou aguarde a ativação do seu chip.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {devices.map((device, index) => {
                const statusCfg = STATUS_CONFIG[device.status] || STATUS_CONFIG.OFFLINE;
                const StatusIcon = statusCfg.icon;
                // Meta status derivado (backend sempre manda 'overall').
                const metaOverall = device.meta_status?.overall || 'UNKNOWN';
                const metaCfg = META_STATUS_CONFIG[metaOverall] || META_STATUS_CONFIG.UNKNOWN;
                const MetaIcon = metaCfg.icon;
                const originCfg = ORIGIN_BADGE[device.origin];
                return (
                  <Box key={device.id}>
                    <ListItemButton
                      onClick={() => setMetaModalDevice(device)}
                      sx={{ px: 3, py: 2, '&:hover': { bgcolor: '#f8fafc' } }}
                    >
                      {/* Ícone de status */}
                      <Box
                        sx={{
                          width: 40, height: 40, borderRadius: '10px', mr: 2, flexShrink: 0,
                          bgcolor: statusCfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >
                        <StatusIcon size={18} color={statusCfg.color} />
                      </Box>

                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body1" fontWeight={700} color="#0f172a">
                              {device.name}
                            </Typography>
                            {/* Badge Meta Status — clicável (mesmo click do card) */}
                            <Chip
                              icon={<MetaIcon size={12} />}
                              label={metaCfg.label}
                              size="small"
                              sx={{
                                bgcolor: metaCfg.bg, color: metaCfg.color,
                                border: `1px solid ${metaCfg.border}`,
                                fontWeight: 700, fontSize: '0.68rem', height: 22,
                                borderRadius: '6px',
                                '& .MuiChip-icon': { color: metaCfg.color, ml: '4px' },
                              }}
                            />
                            {originCfg && (
                              <Tooltip title={originCfg.title} arrow>
                                <Chip
                                  label={originCfg.label}
                                  size="small"
                                  sx={{
                                    bgcolor: originCfg.bg, color: originCfg.color,
                                    border: `1px solid ${originCfg.border}`,
                                    fontWeight: 700, fontSize: '0.68rem', height: 22,
                                    borderRadius: '6px',
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        }
                        secondary={
                          <Stack direction="row" spacing={1.5} alignItems="center" mt={0.3}>
                            <FiPhone size={12} color="#94a3b8" />
                            <Typography variant="caption" color="#64748b">
                              {device.phone_number}
                            </Typography>
                          </Stack>
                        }
                      />

                      <Stack direction="row" spacing={2} alignItems="center">
                        {/* Health Score */}
                        <Box sx={{ textAlign: 'right', minWidth: 60 }}>
                          <Typography variant="caption" color="#94a3b8" display="block">
                            Saúde
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={device.health_score >= 70 ? '#16a34a' : device.health_score >= 40 ? '#d97706' : '#dc2626'}
                          >
                            {device.health_score ?? 100}%
                          </Typography>
                        </Box>

                        {/* Badge de status */}
                        <Chip
                          label={statusCfg.label}
                          size="small"
                          sx={{
                            bgcolor: statusCfg.bg, color: statusCfg.color,
                            fontWeight: 700, fontSize: '0.72rem', height: 24, borderRadius: '6px'
                          }}
                        />
                      </Stack>
                    </ListItemButton>
                    {index < devices.length - 1 && <Divider sx={{ mx: 3 }} />}
                  </Box>
                );
              })}
            </List>
          )}
        </Paper>
      </Box>

      {/* ── Modal detalhado de Meta Status ──────────────────────────────────── */}
      <Dialog
        open={Boolean(metaModalDevice)}
        onClose={() => setMetaModalDevice(null)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {metaModalDevice && (() => {
          const overall = metaModalDevice.meta_status?.overall || 'UNKNOWN';
          const cfg = META_STATUS_CONFIG[overall] || META_STATUS_CONFIG.UNKNOWN;
          const Icon = cfg.icon;
          const detail = metaModalDevice.meta_status?.detail || {};
          const updatedAt = metaModalDevice.meta_status?.updated_at;
          const fmtDetail = (v) => (v || 'UNKNOWN').replace(/_/g, ' ').toLowerCase();
          return (
            <>
              <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <FiShield size={18} color="#334155" />
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>Status Meta do chip</span>
                </Stack>
                <Typography variant="caption" color="#64748b">
                  {metaModalDevice.name} · {metaModalDevice.phone_number}
                </Typography>
              </DialogTitle>
              <DialogContent>
                {/* Hero do overall */}
                <Paper elevation={0} sx={{
                  p: 2.5, mb: 2, borderRadius: 2,
                  bgcolor: cfg.bg, border: `1px solid ${cfg.border}`,
                }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Icon size={22} color={cfg.color} style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="body1" fontWeight={800} color={cfg.color}>
                        {cfg.label}
                      </Typography>
                      <Typography variant="caption" color="#475569" sx={{ display: 'block', mt: 0.5, lineHeight: 1.5 }}>
                        {cfg.desc}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {/* Detalhe dos 3 status raw */}
                <Typography variant="caption" fontWeight={700} sx={{
                  color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase',
                  display: 'block', mb: 1,
                }}>
                  Detalhamento (fonte: Meta Graph API)
                </Typography>
                <Stack spacing={1}>
                  {Object.entries(META_DETAIL_LABEL).map(([key, label]) => (
                    <Stack key={key} direction="row" justifyContent="space-between" alignItems="center" sx={{
                      p: 1.2, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0',
                    }}>
                      <Typography variant="caption" color="#334155">{label}</Typography>
                      <Chip
                        label={fmtDetail(detail[key])}
                        size="small"
                        sx={{
                          bgcolor: 'white', border: '1px solid #cbd5e1',
                          fontSize: '0.68rem', fontWeight: 700,
                          // Limites de palavra (\b) importam aqui: sem eles
                          // `/VERIFIED/` casava dentro de "NOT_VERIFIED" e o
                          // modal pintava de VERDE justamente o número que a
                          // Meta diz não estar verificado. `_` conta como
                          // caractere de palavra em JS, então \bVERIFIED\b não
                          // casa em NOT_VERIFIED — e \bCONNECTED\b não casa em
                          // DISCONNECTED.
                          color: (detail[key] || '').match(/\b(APPROVED|VERIFIED|CONNECTED)\b/i) ? '#16a34a'
                              : (detail[key] || '').match(/\b(REJECTED|NOT_VERIFIED|EXPIRED|DISCONNECTED)\b/i) ? '#dc2626'
                              : (detail[key] || '').match(/\b(PENDING|NOT_STARTED|MIGRATED)\b/i) ? '#d97706'
                              : '#64748b',
                        }}
                      />
                    </Stack>
                  ))}
                </Stack>

                {updatedAt && (
                  <Typography variant="caption" color="#94a3b8" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                    Última sincronização: {new Date(updatedAt).toLocaleString('pt-BR')}
                  </Typography>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setMetaModalDevice(null)} sx={{ textTransform: 'none', color: '#64748b' }}>
                  Fechar
                </Button>
                <Button
                  onClick={() => handleSyncMeta(metaModalDevice.id)}
                  disabled={metaSyncing}
                  startIcon={metaSyncing ? <CircularProgress size={12} /> : <FiRefreshCw size={12} />}
                  variant="contained"
                  sx={{
                    bgcolor: '#25d366', color: 'white', fontWeight: 700, textTransform: 'none',
                    borderRadius: 2, boxShadow: 'none',
                    '&:hover': { bgcolor: '#1ea855', boxShadow: 'none' },
                  }}
                >
                  {metaSyncing ? 'Sincronizando…' : 'Sincronizar agora'}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

    </Box>
  );
}
